import React, {
  createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { URL_SEARCH_PARAMS, SU_API } from 'src/constants/api';
import { KatSpinner } from '@amzn/katal-react';
import { ROUTE_SEARCH_PARAM, ROUTES } from 'src/constants/route';
import { ClientLog } from 'src/constants/clientLog';
import ClientLogger from 'src/utils/logger';
import { redirectToSignIn } from 'src/utils/authRedirect';
import { useGetSellerContextQuery } from 'src/redux/slices/api/su';
import { setApiSurface } from 'src/redux/slices/api/apiSurface';
import { MbmProvider } from '@amzn/react-arb-tools';
import { getMbmOptions } from 'src/utils/translation';
import arbManifest from '../../build/translations/arbManifest';

interface ClientData {
  clientId: string;
  selLocale: string | undefined;
  marketplaceCountryCode: string | undefined;
}

export const GATED_CLIENT_ID = 'SELLER_UNIVERSITY_CAMPUS';
export const UNGATED_CLIENT_ID = 'SELLER_UNIVERSITY_CAMPUS_UNGATED';
export const WIDGET_DEFAULT_CLIENT_ID = 'SELLER_UNIVERSITY_CAMPUS_WIDGET';

const ClientContext = createContext<ClientData>({ clientId: UNGATED_CLIENT_ID, selLocale: undefined, marketplaceCountryCode: undefined });

const LOCALE_FORMAT = /^[a-z]{2}_[A-Z]{2}$/;

/**
 * Reads mons_sel_locale from the initial URL once at app load.
 * Only valid for ungated users — gated users use Mons Accept-Language header.
 */
export const getSelLocaleFromUrl = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const value = new URLSearchParams(window.location.search).get(URL_SEARCH_PARAMS.MONS_SEL_LOCALE);
  return value && LOCALE_FORMAT.test(value) ? value : undefined;
};

/**
 * Check if the current route is a widget route.
 * SSR-safe — returns false when window is undefined.
 */
const isWidgetRoute = (): boolean => (
  typeof window !== 'undefined'
  && window.location.pathname.startsWith(ROUTES.WIDGETS.BASE)
);

/**
 * Reads the embedder-supplied `unauthenticated` param on widget routes.
 * Matches useWidgetPageData's semantics: only the literal 'true' counts as unauthenticated.
 * No window guard needed — only called from an effect, and only when isWidget is already true.
 */
const isWidgetUnauthenticated = (): boolean => (
  new URLSearchParams(window.location.search).get('unauthenticated') === 'true'
);

/**
 * Extract clientId from URL parameters for widget routes.
 * Returns the URL-param clientId if valid, otherwise WIDGET_DEFAULT_CLIENT_ID.
 */
export const getClientIdFromUrl = (): string => {
  if (typeof window === 'undefined') {
    return WIDGET_DEFAULT_CLIENT_ID;
  }

  // Only extract clientId from URL params for widget routes
  if (!window.location.pathname.startsWith(ROUTES.WIDGETS.BASE)) {
    return WIDGET_DEFAULT_CLIENT_ID;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams.get(ROUTE_SEARCH_PARAM.CLIENT_ID);

  return clientId && /^[a-zA-Z0-9_\-.]{1,100}$/.test(clientId)
    ? clientId
    : WIDGET_DEFAULT_CLIENT_ID;
};

/**
 * ClientProvider — sets the default clientId once at app load and locks it for the session.
 *
 * Children are NOT rendered until clientId is resolved. This prevents content API calls
 * (e.g. useGetLearningPathwayQuery) from firing with a stale/default clientId before
 * the seller-context API response determines the correct value.
 *
 * For widget routes: clientId is extracted from URL params immediately — no blocking.
 *
 * For campus routes: clientId is determined by the GetSellerContext API response:
 *   - If customerId AND merchantId are present → GATED (SELLER_UNIVERSITY_CAMPUS)
 *   - If either is missing → UNGATED (SELLER_UNIVERSITY_CAMPUS_UNGATED)
 *
 * The clientId is locked via useRef after the first determination and never changes
 * for the remainder of the session. This prevents mid-session clientId flips when
 * a gated user's auth expires (SUOS uses clientId to detect this and returns 412).
 */
export const ClientProvider = ({ children }: { children: React.ReactNode }) => {
  // Captured once at mount — stable for the lifetime of the provider.
  // Widget and campus routes are separate Mons configs that trigger full page reloads,
  // so this value never changes within a single provider mount.
  const [isWidget] = useState(() => isWidgetRoute());

  // Skip the GetSellerContext API call entirely for widget routes —
  // widgets don't need seller context and resolve clientId from URL params.
  const { data, isLoading, isError } = useGetSellerContextQuery(undefined, { skip: isWidget });
  const customerId = data?.obfuscatedCustomerId;
  const merchantId = data?.obfuscatedMerchantId;
  const isLocked = useRef(false);

  const [clientId, setClientId] = useState(() => {
    if (isWidget) {
      return getClientIdFromUrl();
    }
    return UNGATED_CLIENT_ID;
  });

  // For widget routes, selLocale is resolved immediately from the URL.
  // For campus routes, selLocale is resolved after seller-context responds (see useEffect below).
  const [selLocale, setSelLocale] = useState<string | undefined>(() => (
    isWidget ? getSelLocaleFromUrl() : undefined
  ));

  // Widget routes are ready immediately; campus routes are ready once locked
  const [isReady, setIsReady] = useState(isWidget);

  useEffect(() => {
    // Only set once, only for campus routes, only after seller-context has resolved
    if (!isLoading && !isLocked.current && !isWidget) {
      const isGated = !isError && customerId && merchantId;
      const resolvedClientId = isGated ? GATED_CLIENT_ID : UNGATED_CLIENT_ID;

      if (resolvedClientId === UNGATED_CLIENT_ID && !isError) {
        const countryCode = data?.marketplaceCountryCode;
        if (!countryCode) {
          ClientLogger.error(ClientLog.APICriticalError, new Error('marketplaceCountryCode missing from seller-context'), {
            marketplaceId: data?.obfuscatedMarketplaceId,
            // This edge case is only reached on the ungated campus resolution path.
            surface: 'Ungated',
          });
          redirectToSignIn({ message: 'Ungated campus blocked: missing marketplaceCountryCode', url: window.location.href, endpoint: SU_API.SELLER_CONTEXT });
          isLocked.current = true;
          return;
        }
      }
      setClientId(resolvedClientId);

      // Resolve selLocale for ungated users only:
      // 1. mons_sel_locale from URL (user explicitly switched locale)
      // 2. defaultLanguageCode from seller-context (marketplace default, validated)
      // 3. undefined (don't send sel_locale in API calls)
      if (resolvedClientId !== GATED_CLIENT_ID) {
        const validDefault = data?.defaultLanguageCode && LOCALE_FORMAT.test(data.defaultLanguageCode)
          ? data.defaultLanguageCode
          : undefined;
        const urlLocale = getSelLocaleFromUrl();
        if (!urlLocale && validDefault) {
          // mons_sel_locale absent — append it and reload so the LEGO nav language
          // picker aligns with the locale the campus will render in.
          // Issue ref: https://t.corp.amazon.com/P427934409/overview
          const url = new URL(window.location.href);
          url.searchParams.set(URL_SEARCH_PARAMS.MONS_SEL_LOCALE, validDefault);
          window.location.replace(url.toString());
          isLocked.current = true;
          return;
        }
        setSelLocale(urlLocale ?? validDefault);
      }

      isLocked.current = true;
      setIsReady(true);
    }
  }, [isLoading, isError, customerId, merchantId, isWidget, data?.defaultLanguageCode]);

  // Publish the resolved surface so the non-React API layer can log it.
  // Widget auth state comes from the embedder-supplied `unauthenticated` param — the host
  // page's best-effort signal, since widget routes skip seller-context (see skip: isWidget).
  useEffect(() => {
    if (isWidget) {
      setApiSurface(isWidgetUnauthenticated() ? 'Widget:Ungated' : 'Widget:Gated');
      return;
    }
    setApiSurface(clientId === GATED_CLIENT_ID ? 'Gated' : 'Ungated');
  }, [clientId, isWidget]);

  const value = useMemo(() => ({
    clientId,
    // selLocale applies to all non-gated clients (ungated campus + widget).
    // Gated users rely on Mons Accept-Language header instead.
    selLocale: clientId !== GATED_CLIENT_ID ? selLocale : undefined,
    marketplaceCountryCode: data?.marketplaceCountryCode,
  }), [clientId, selLocale, data?.marketplaceCountryCode]);

  // Block rendering children until clientId is resolved.
  // This ensures no content API calls fire with a stale clientId.
  // For widget routes this is a no-op (ready on first render).
  // For campus routes this blocks until GetSellerContext resolves (~one network round-trip).
  if (!isReady) {
    return <KatSpinner size="large" />;
  }

  // selLocale is now resolved before children render.
  // For campus ungated: marketplace default from seller-context if mons_sel_locale not present.
  // For campus gated / no locale: falls back to html lang attribute.
  // This ensures MbmProvider initializes with the correct locale on first render.
  // arb-tools requires BCP 47 hyphen format (ja-JP), but selLocale uses underscore (ja_JP).
  const mbmLocale = selLocale ? selLocale.replace('_', '-') : undefined;
  const mbmOptions = getMbmOptions(arbManifest, mbmLocale);

  return (
    <ClientContext.Provider value={value}>
      <MbmProvider {...mbmOptions}>
        {children}
      </MbmProvider>
    </ClientContext.Provider>
  );
};

export const useClientId = () => useContext(ClientContext).clientId;
export const useSelLocale = () => useContext(ClientContext).selLocale;
export const useMarketplaceCountryCode = () => useContext(ClientContext).marketplaceCountryCode;
